/* eslint-disable max-statements-per-line */
/* eslint-disable @typescript-eslint/no-use-before-define */
// TODO: integrate into ia-utils and remove from here
import { isDate, isPlainObject, isRegex } from '@develop/ia-utils'

const StandardBuiltInObject = {
  Array: '[object Array]',
  ArrayBuffer: '[object ArrayBuffer]',
  Boolean: '[object Boolean]',
  Date: '[object Date]',
  Error: '[object Error]',
  Function: '[object Function]',
  Generator: '[object Generator]',
  GeneratorFunction: '[object GeneratorFunction]',
  Map: '[object Map]',
  Number: '[object Number]',
  Object: '[object Object]',
  Promise: '[object Promise]',
  RegExp: '[object RegExp]',
  Set: '[object Set]',
  String: '[object String]',
  Symbol: '[object Symbol]',
  WeakMap: '[object WeakMap]',
  WeakSet: '[object WeakSet]',
} as const

const OtherBuiltInObject = {
  BigInt: '[object BigInt]',
  DataView: '[object DataView]',
  JSON: '[object JSON]',
  Math: '[object Math]',
  Proxy: '[object Proxy]',
} as const

const GlobalValue = {
  global: '[object global]', // Node.js
  Window: '[object Window]', // Browsers
  globalThis: '[object globalThis]',
} as const

export const StandardObject = {
  ...StandardBuiltInObject,
  ...OtherBuiltInObject,
  ...GlobalValue,
} as const

export type PlainObject = Record<PropertyKey, any>

type TypedArray =
  | Uint8Array
  | Int8Array
  | Uint8ClampedArray
  | Int16Array
  | Uint16Array
  | Int32Array
  | Uint32Array
  | Float32Array
  | Float64Array
  | BigInt64Array
  | BigUint64Array

export const toTypeString = (value: unknown): string => {
  return Object.prototype.toString.call(value)
}

export const isMap = (val: any): val is Map<any, any> => {
  return toTypeString(val) === StandardObject.Map
}

export const isSet = (val: any): val is Set<any> => {
  return toTypeString(val) === StandardObject.Set
}

export const isTypedArray = (value: unknown): value is TypedArray => {
  return ArrayBuffer.isView(value) && !(value instanceof DataView)
}

export const isEqual = (x: unknown, y: unknown): boolean => {
  if (Object.is(x, y)) {
    return true
  }

  if (typeof x !== typeof y) {
    return false
  }

  if (isDate(x) && isDate(y)) {
    return x.getTime() === y.getTime()
  }

  if (isRegex(x) && isRegex(y)) {
    return x.toString() === y.toString()
  }

  if (Array.isArray(x) && Array.isArray(y)) {
    return isEqualArray(x, y)
  }

  if (isPlainObject(x) && isPlainObject(y)) {
    return isEqualPlainObject(x, y)
  }

  if ((isMap(x) && isMap(y)) || (isSet(x) && isSet(y))) {
    return isEqualArray([...x], [...y])
  }

  if (x instanceof ArrayBuffer && y instanceof ArrayBuffer) {
    return isEqualDataView(new DataView(x), new DataView(y))
  }

  if (x instanceof DataView && y instanceof DataView) {
    return isEqualDataView(x, y)
  }

  if (isTypedArray(x) && isTypedArray(y)) {
    if (x.byteLength !== y.byteLength) {
      return false
    }
    return isEqualArray(x, y)
  }

  return false
}

// Helpers

const isEqualArray = <T>(x: T[] | TypedArray, y: T[] | TypedArray) => {
  if (x.length !== y.length) {
    return false
  }
  return x.every((el, idx) => isEqual(el, y[idx]))
}

const isEqualPlainObject = (x: PlainObject, y: PlainObject) => {
  const keysX = Reflect.ownKeys(x)
  const keysY = Reflect.ownKeys(y)

  if (!isEqual(keysX, keysY)) {
    return false
  }

  for (const key of keysX) {
    if (!Reflect.has(y, key)) {
      return false
    }
    if (!isEqual(x[key], y[key])) {
      return false
    }
  }

  return true
}

const isEqualDataView = (a: DataView, b: DataView) => {
  if (a.byteLength !== b.byteLength) { return false }
  for (let offset = 0; offset < a.byteLength; offset++) {
    if (a.getUint8(offset) !== b.getUint8(offset)) {
      return false
    }
  }
  return true
}
